import { PageProps } from "gatsby";
import { GatsbyImageProps } from "gatsby-plugin-image";
import { Dispatch, SetStateAction } from "react";

export interface ICategory {
    category: string
}

export const GetSlug = (category: string, slug: string) => {

    if (category === "Story") {
        return `/stories/${slug}`
    }
    if (category === "Feature") {
        return `/features/${slug}`
    }

    if (category === "News") {
        return `/blog/news/${slug}`
    }

    if (category === "SuccessStory") {
        return `/success-stories/${slug}`
    }
    if (category === "Legal") {
        return `/legal/${slug}`
    }
    return `/blog/${category}/${slug}`
}


export interface IZammadArticle {
    subject: string
    body: string
    type: string
    internal: boolean
}

export interface IZammadTicket {
    title: string,
    group: string,
    customer_id: string,
    customer: string,
    article: IZammadArticle
}

export const CreateZammadTicket = (mail: string, subject: string, body?: string, group?: string) => {
    return new Promise<boolean>((resolve, reject) => {
        const ticket: IZammadTicket = {
            title: subject,
            group: group ?? "Support",
            customer: mail,
            customer_id: `guess:${mail}`,
            article: {
                subject: subject,
                body: body as string,
                type: "note",
                internal: false
            }
        };

        fetch("https://api.support.easylife365.cloud/v1/tickets", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ticket)
        }).then(response => {
            if (response.ok)
                resolve(true);
            else
                reject(false);
        });
    });
};

type HubspotFormConfig = {
    region: string;
    portalId: string;
    formId: string;
    target: string;
};


export const HUBSPOT_FORMS: Record<string, HubspotFormConfig> = {
    en: {
        region: "eu1",
        portalId: "25588300",
        formId: "e3a4ecb5-036c-48a7-9481-af1fe05d7d24",
        target: "#formContainer",
    },
    de: {
        region: "eu1",
        portalId: "25588300",
        formId: "b41b3c68-3bb6-4a2b-9c13-744b75cebda9",
        target: "#formContainer",
    },
};


export interface GetAQuoteButtonProps {
    enabled: boolean;
    numberOfUsers: number;
    premiumSupport: boolean;
    yearlyCost: number;
    currency: string;
    maxPackage: boolean;
    hasFieldWorkers: boolean,
    meetPrice?: number,
    collabPrice?: number,
    mailPrice?: number,
    isSuccess: boolean,
    isFailure: boolean,
    isLoading: boolean,
    isExecutingRequest: boolean,
    setIsSuccess: Dispatch<SetStateAction<boolean>>,
    setIsFailure: Dispatch<SetStateAction<boolean>>,
    setLoading: Dispatch<SetStateAction<boolean>>,
    setIsExecutingRequest: Dispatch<SetStateAction<boolean>>,
    setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>,
    errors
}

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;


export type HeadProps = PageProps & {
    data: {
        markdownRemark: {
            frontmatter: {
                title: string;
                slug: string;
                category: string;
                authorid: string;
                description: string;
            };
        };
    };
}

export type BaseFrontmatter = {
    title: string;
    date: string;
    category: string;
    subtitle?: string;
    blogimage?: {
        childImageSharp?: {
            gatsbyImageData: GatsbyImageProps;
        };
        alternativeText?: string;
    };
    slug: string;
    description: string;
    sector?: string;
    language?: string;
    successstoryfile?: {
        publicURL: string;
    };
    authorid?: string;
    serieslabel?: string;
    seriesarticles?: SeriesArticle[];
    videoId?:String;
    videoTitle?: String;
};

export type BaseNode<FrontmatterType> = {
    id: string;
    html: string;
    frontmatter: FrontmatterType;
    fields: {
        language: string;
    };
    author?: Author; // Optional author field for articles
};

export type ArticleNode = BaseNode<BaseFrontmatter>;

export type Edge<NodeType> = {
    node: NodeType;
};
export type ArticleEdge = Edge<ArticleNode>;
export type SuccessStoryEdge = Edge<ArticleNode>;

export type EnumerateProps<EdgeType> = {
    data: {
        allMarkdownRemark: {
            edges: EdgeType[];
        };
        [key: string]: any; // Additional optional fields
    };
    type: "articles" | "successStories"; // Discriminator
};

export interface AuthorNode {
    node: Author
}

export interface Author {
    authorname: string;
    description: string;
    email: string;
    id: string;
    imagename: string;
    authorImage?: AuthorImage;
}

export interface AuthorImageNode {
    node: AuthorImage
}

export interface AuthorImage {
    childImageSharp: {
        gatsbyImageData: GatsbyImageProps;
    };
    name: string;
    ext: string;
}

export interface SeriesArticle {
    id: string;
    frontmatter: {
        date: string;
        slug: string;
        title: string;
        serieslabel: string;
    };
}


export type BaseQueryData<EdgeType> = {
    allMarkdownRemark: {
        edges: EdgeType[];
    };
    [key: string]: any; // Allows for additional optional fields like author data or images
};

export type ArticleQueryData = BaseQueryData<ArticleEdge> & {
    authordetails: {
        edges: { node: Author }[];
    };
    authorimages: {
        edges: {
            node: AuthorImage;
        }[];
    };
};


export type SuccessStoryQueryData = BaseQueryData<SuccessStoryEdge>;

export type QueryData = ArticleQueryData | SuccessStoryQueryData;



export type ArticleSideBarProps = {
    currentSlug: string;
}


export type IndexQueryData = {
    allMarkdownRemark: {
        edges: ArticleEdge[];
    };
}

export interface AuthorProfile {
    authorname: string;
    email: string;
    description: string;
    imagename: string;
}
export interface LocaleAuthorData {
    locale: string;
    authors: AuthorProfile[];
}

export interface FileNode {
    name: string;
    publicURL: string;
}

export interface FileEdge {
    node: FileNode;
}

export interface AllFileData {
    allFile: {
        edges: FileEdge[];
    };
}


export const GetNonBlogCategories = (): string[] => {
    return (process.env.GATSBY_NON_BLOG_CATEGORIES || "")
        .split(',')
        .map((category) => category.trim().toLowerCase()) // Normalize and trim spaces
        .filter((category) => category !== ""); // Exclude empty strings
};
